import { useEffect, useState } from "react";
import { api, apiError, API } from "../../lib/api";
import { StoreLayout } from "../../components/StoreLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  ArrowLeft, ArrowRight, MagnifyingGlass, CheckCircle, Camera, VideoCamera, Wrench,
  DeviceMobile, Sparkle, SpinnerGap,
} from "@phosphor-icons/react";
import { compressMedia } from "../../lib/media";
import { categoryImage } from "../../lib/storefront";
import { toast } from "sonner";

const IMG_BASE = API.replace(/\/api$/, "");
const DRAFT = "sell_draft";
const STEPS = ["Device", "Condition", "Offer", "Pickup"];

const emptyForm = {
  category: null, brand: null, model: null, manual: null,
  answers: {}, media: [], estimate: null,
  name: "", email: "", phone: "", address: "", pincode: "",
  agree: false, otpToken: "", pickup_date: "", pickup_slot: "",
};

export default function SellDevices() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => {
    try { return { ...emptyForm, ...(JSON.parse(localStorage.getItem(DRAFT) || "null") || {}) }; } catch { return emptyForm; }
  });
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => { localStorage.setItem(DRAFT, JSON.stringify(form)); }, [form]);

  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [labels, setLabels] = useState([]);
  const [slots, setSlots] = useState([]);
  const [terms, setTerms] = useState(null);
  const [bq, setBq] = useState(""); const [mq, setMq] = useState("");
  const [busy, setBusy] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => { api.get("/sell/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => { if (form.category) api.get(`/sell/brands?category_id=${form.category.id}`).then((r) => setBrands(r.data)); }, [form.category]);
  useEffect(() => { if (form.brand) api.get(`/sell/models?brand_id=${form.brand.id}`).then((r) => setModels(r.data)); }, [form.brand]);
  useEffect(() => {
    if (form.category) {
      api.get(`/sell/questions?category_id=${form.category.id}&brand_id=${form.brand?.id || ""}`).then((r) => setQuestions(r.data));
      api.get(`/sell/image-labels?category_id=${form.category.id}`).then((r) => setLabels(r.data));
    }
  }, [form.category, form.brand]);
  useEffect(() => { api.get("/sell/slots").then((r) => setSlots(r.data)); api.get("/sell/terms").then((r) => setTerms(r.data)); }, []);

  const pct = Math.round(((step + 1) / STEPS.length) * 100);
  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const answersPayload = () => Object.entries(form.answers).map(([qid, v]) =>
    Array.isArray(v) ? { question_id: qid, values: v } : { question_id: qid, value: v });

  const runEstimate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sell/estimate", {
        category_id: form.category.id, brand_id: form.brand?.id || "", model_id: form.model?.id || "",
        answers: answersPayload(),
      });
      set({ estimate: data }); next();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const uploadFile = async (label, mediaType, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const compressed = await compressMedia(file, mediaType);
      const fd = new FormData(); fd.append("file", compressed);
      const { data } = await api.post(`/sell/media?label=${encodeURIComponent(label)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set({ media: [...form.media.filter((m) => m.label !== label), { ...data, mediaType }] });
      toast.success(`${label} uploaded`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Upload failed"); }
    setBusy(false);
  };

  const checkPincode = async () => {
    if (!/^\d{6}$/.test(form.pincode)) { toast.error("Enter a valid 6-digit pincode"); return false; }
    try {
      const { data } = await api.get(`/public/serviceability/${form.pincode}`);
      if (!data.serviceable) { toast.error("Currently unavailable in your area."); return false; }
      const sellOk = (data.services || []).some((l) => l.slug === "sell_appliance");
      if (!sellOk) { toast.error(`Sell Devices isn't available at ${form.pincode}${data.city ? ` (${data.city})` : ""} yet.`); return false; }
      return true;
    } catch { toast.error("Could not verify pincode"); return false; }
  };

  const sendOtp = async () => {
    if (!form.email) { toast.error("Enter your email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/sell/otp/send", { email: form.email });
      set({ otpToken: data.token }); setOtpSent(true); toast.success("OTP sent to your email");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const verifyOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sell/otp/verify", { email: form.email, code: otpCode });
      set({ otpToken: data.token }); setOtpVerified(true); toast.success("Email verified");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const submit = async () => {
    if (!form.agree) { toast.error("Please accept the Terms & Conditions"); return; }
    if (!otpVerified) { toast.error("Please verify your email"); return; }
    if (!form.pickup_date || !form.pickup_slot) { toast.error("Select a pickup date and slot"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/sell/orders", {
        category_id: form.category.id, brand_id: form.brand?.id || "", model_id: form.model?.id || "",
        manual_model: form.manual, name: form.name, email: form.email, phone: form.phone,
        address: form.address, pincode: form.pincode, answers: answersPayload(),
        media_ids: form.media.map((m) => m.id), terms_version: terms?.version || 1,
        pickup_date: form.pickup_date, pickup_slot: form.pickup_slot, otp_token: form.otpToken,
      });
      setConfirmation(data); localStorage.removeItem(DRAFT);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const manualValid = !!form.manual && Object.values(form.manual).some(Boolean);
  const deviceDone = !!form.category && (!!form.model || manualValid);
  const mandatoryQsAnswered = questions.filter((q) => q.mandatory).every((q) => {
    const v = form.answers[q.id]; return Array.isArray(v) ? v.length > 0 : (v !== undefined && v !== "");
  });
  const requiredMediaDone = labels.filter((l) => l.required).every((l) => form.media.some((m) => m.label === l.label));

  const deviceLabel = [form.brand?.name, form.model?.name || (manualValid && Object.values(form.manual).filter(Boolean).join(" "))].filter(Boolean).join(" ");

  /* ---------- Confirmation ---------- */
  if (confirmation) {
    return (
      <StoreLayout>
        <div className="max-w-3xl mx-auto px-4 py-10">
          <div className="text-center" data-testid="sell-confirmation">
            <CheckCircle size={56} weight="fill" className="text-[#166534] mx-auto" />
            <h2 className="font-[Outfit] text-3xl font-extrabold mt-3">Request confirmed!</h2>
            <p className="text-[#475569] mt-2">Booking ID <b data-testid="sell-booking-id">{confirmation.booking_id}</b></p>
            <div className="mt-6 bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-5 text-left text-sm max-w-md mx-auto space-y-1.5">
              <div className="flex justify-between"><span className="text-[#475569]">Device</span><span className="font-semibold">{confirmation.category_name} {confirmation.brand_name} {confirmation.model_name}</span></div>
              <div className="flex justify-between"><span className="text-[#475569]">Estimated value</span><span className="font-semibold">₹{confirmation.estimated_price.toLocaleString("en-IN")} ({confirmation.tier_name})</span></div>
              <div className="flex justify-between"><span className="text-[#475569]">Pickup</span><span className="font-semibold">{confirmation.pickup_date}, {confirmation.pickup_slot}</span></div>
            </div>
            <p className="text-xs text-[#94a3b8] mt-4 max-w-md mx-auto">Our team will arrive for pickup & inspection. Final price is confirmed after QC. A confirmation email has been sent.</p>
            <Button onClick={() => { setForm(emptyForm); setStep(0); setConfirmation(null); setOtpSent(false); setOtpVerified(false); setOtpCode(""); }} className="rounded-xl mt-6 bg-[#1E293B] hover:bg-[#0f172a]" data-testid="sell-new-request">Sell another device</Button>
          </div>
        </div>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      <div className="max-w-3xl mx-auto px-4 py-6 md:py-10" data-testid="sell-wizard">
        {/* Progress — 4 steps */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            {STEPS.map((label, i) => (
              <div key={label} className="flex-1">
                <div className={`h-1.5 rounded-full transition-all duration-500 ${i <= step ? "bg-[#4F46E5]" : "bg-[#E2E8F0]"}`} />
                <span className={`text-[11px] font-semibold mt-1.5 block ${i === step ? "text-[#4F46E5]" : i < step ? "text-[#1E293B]" : "text-[#94a3b8]"}`}>{i + 1}. {label}</span>
              </div>
            ))}
          </div>
        </div>

        {step > 0 && (
          <button onClick={back} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#475569] hover:text-[#4F46E5] mb-4" data-testid="sell-back">
            <ArrowLeft size={16} weight="bold" /> Back
          </button>
        )}

        {/* ===== STEP 0 — Device (Category + Brand + Model) ===== */}
        {step === 0 && (
          <div data-testid="sell-step-device">
            <h1 className="font-[Outfit] text-3xl md:text-4xl font-extrabold tracking-tight">Sell your device</h1>
            <p className="text-[#475569] mt-2">Pick your device, brand & model. Instant estimate, free pickup, price confirmed after inspection.</p>

            {/* Category */}
            <h3 className="font-[Outfit] font-bold text-lg mt-7">1. Choose category</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
              {cats.map((c) => (
                <button key={c.id} onClick={() => { set({ category: c, brand: null, model: null, manual: null, answers: {}, media: [], estimate: null }); setShowManual(false); setBq(""); setMq(""); }}
                  data-testid={`sell-cat-${c.code}`}
                  className={`group relative aspect-[4/3] rounded-2xl overflow-hidden text-left transition-all duration-300 ${form.category?.id === c.id ? "ring-4 ring-[#4F46E5] ring-offset-2" : "shadow-sm hover:shadow-xl hover:-translate-y-0.5"}`}>
                  <img src={categoryImage(c.name)} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                  <div className="absolute bottom-0 p-3 text-white font-[Outfit] font-bold text-sm">{c.name}</div>
                  {form.category?.id === c.id && <CheckCircle size={20} weight="fill" className="absolute top-2 right-2 text-white" />}
                </button>
              ))}
            </div>

            {/* Brand */}
            {form.category && (
              <div className="mt-8">
                <h3 className="font-[Outfit] font-bold text-lg">2. Choose brand</h3>
                <div className="relative mt-3">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
                  <Input value={bq} onChange={(e) => setBq(e.target.value)} placeholder="Search brand" className="rounded-xl pl-9" data-testid="sell-brand-search" />
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4">
                  {brands.filter((b) => b.name.toLowerCase().includes(bq.toLowerCase())).map((b) => (
                    <button key={b.id} onClick={() => { set({ brand: b, model: null, manual: null }); setShowManual(false); setMq(""); }} data-testid={`sell-brand-${b.id}`}
                      className={`aspect-square rounded-2xl border-2 flex flex-col items-center justify-center gap-2 font-semibold text-sm transition-all duration-200 ${form.brand?.id === b.id ? "border-[#4F46E5] bg-[#4F46E5]/5" : "border-[#E2E8F0] hover:border-[#4F46E5]"}`}>
                      {b.logo ? <img src={b.logo} alt={b.name} className="h-8 object-contain" /> : <DeviceMobile size={26} className="text-[#4F46E5]" />}
                      {b.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Model */}
            {form.brand && (
              <div className="mt-8">
                <h3 className="font-[Outfit] font-bold text-lg">3. Choose model</h3>
                <div className="relative mt-3">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
                  <Input value={mq} onChange={(e) => setMq(e.target.value)} placeholder="Search model" className="rounded-xl pl-9" data-testid="sell-model-search" />
                </div>
                <div className="space-y-2 mt-4">
                  {models.filter((m) => m.name.toLowerCase().includes(mq.toLowerCase())).map((m) => (
                    <button key={m.id} onClick={() => { set({ model: m, manual: null }); setShowManual(false); }} data-testid={`sell-model-${m.id}`}
                      className={`w-full text-left px-4 py-3 rounded-xl border-2 font-medium transition-all duration-200 ${form.model?.id === m.id ? "border-[#4F46E5] bg-[#4F46E5]/5" : "border-[#E2E8F0] hover:border-[#4F46E5]"}`}>
                      {m.name}
                    </button>
                  ))}
                </div>
                <button onClick={() => setShowManual((v) => !v)} className="mt-4 text-sm font-bold text-[#4F46E5] hover:underline" data-testid="sell-cant-find">Can't find my model?</button>
                {showManual && (
                  <div className="mt-3 border border-[#E2E8F0] rounded-2xl p-4 space-y-3" data-testid="sell-manual">
                    <p className="text-sm font-semibold">Enter details manually</p>
                    {["Capacity", "Size", "Variant"].map((k) => (
                      <Input key={k} placeholder={k} className="rounded-xl"
                        value={form.manual?.[k.toLowerCase()] || ""}
                        onChange={(e) => set({ manual: { ...(form.manual || {}), [k.toLowerCase()]: e.target.value }, model: null })} data-testid={`sell-manual-${k.toLowerCase()}`} />
                    ))}
                  </div>
                )}
              </div>
            )}

            <Button disabled={!deviceDone} onClick={next} className="rounded-xl w-full mt-8 h-12 bg-[#4F46E5] hover:bg-[#4338CA] gap-2 disabled:opacity-50" data-testid="sell-device-continue">
              Continue <ArrowRight size={16} weight="bold" />
            </Button>
          </div>
        )}

        {/* ===== STEP 1 — Condition & Photos ===== */}
        {step === 1 && (
          <div data-testid="sell-step-condition">
            <h2 className="font-[Outfit] text-2xl font-bold">Condition &amp; photos</h2>
            {deviceLabel && <p className="text-sm text-[#475569] mt-1">{form.category?.name} · {deviceLabel}</p>}

            <h3 className="font-[Outfit] font-bold text-base mt-6">Tell us the condition</h3>
            <div className="space-y-5 mt-3">
              {questions.map((q) => (
                <div key={q.id} data-testid={`sell-q-${q.id}`}>
                  <Label className="text-sm font-semibold">{q.text}{q.mandatory && <span className="text-red-500"> *</span>}</Label>
                  <div className="mt-2">
                    {(q.qtype === "radio" || q.qtype === "yesno" || q.qtype === "dropdown") && (
                      <div className="flex flex-wrap gap-2">
                        {q.options.map((o) => (
                          <button key={o.label} onClick={() => set({ answers: { ...form.answers, [q.id]: o.label } })} data-testid={`sell-opt-${q.id}-${o.label}`}
                            className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all duration-200 ${form.answers[q.id] === o.label ? "border-[#4F46E5] bg-[#4F46E5] text-white" : "border-[#E2E8F0] hover:border-[#4F46E5]"}`}>
                            {o.label}
                          </button>
                        ))}
                      </div>
                    )}
                    {q.qtype === "multiselect" && (
                      <div className="flex flex-wrap gap-2">
                        {q.options.map((o) => {
                          const arr = Array.isArray(form.answers[q.id]) ? form.answers[q.id] : [];
                          const on = arr.includes(o.label);
                          return (
                            <button key={o.label} onClick={() => set({ answers: { ...form.answers, [q.id]: on ? arr.filter((x) => x !== o.label) : [...arr, o.label] } })}
                              className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all duration-200 ${on ? "border-[#4F46E5] bg-[#4F46E5] text-white" : "border-[#E2E8F0] hover:border-[#4F46E5]"}`}>
                              {o.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {q.qtype === "text" && (
                      <Input className="rounded-xl" value={form.answers[q.id] || ""} onChange={(e) => set({ answers: { ...form.answers, [q.id]: e.target.value } })} />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <h3 className="font-[Outfit] font-bold text-base mt-8">Upload photos &amp; video</h3>
            <p className="text-[#475569] text-sm mt-1">Optional but recommended — clear photos help us give you a fair, faster offer.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
              {labels.map((l) => {
                const uploaded = form.media.find((m) => m.label === l.label);
                const isVideo = l.media_type === "video";
                return (
                  <label key={l.id} data-testid={`sell-media-${l.label.replace(/\s+/g, "-")}`}
                    className={`relative aspect-square rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-1.5 cursor-pointer overflow-hidden ${uploaded ? "border-[#166534]" : "border-[#CBD5E1] hover:border-[#4F46E5]"}`}>
                    <input type="file" accept={isVideo ? "video/*" : "image/*"} capture="environment" className="hidden"
                      onChange={(e) => { uploadFile(l.label, l.media_type, e.target.files?.[0]); e.target.value = ""; }} />
                    {uploaded ? (
                      isVideo
                        ? <div className="text-center text-[#166534]"><VideoCamera size={26} weight="fill" /><div className="text-[11px] font-semibold mt-1">Uploaded</div></div>
                        : <img src={`${IMG_BASE}${uploaded.url}`} alt={l.label} className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      isVideo ? <VideoCamera size={24} className="text-[#94a3b8]" /> : <Camera size={24} className="text-[#94a3b8]" />
                    )}
                    <span className={`text-[11px] font-semibold ${uploaded && !isVideo ? "absolute bottom-1 left-1 right-1 text-white bg-black/50 rounded px-1 text-center" : "text-[#475569]"}`}>
                      {l.label}{l.required ? " *" : ""}
                    </span>
                    {uploaded && <CheckCircle size={16} weight="fill" className="absolute top-1.5 right-1.5 text-[#166534] bg-white rounded-full" />}
                  </label>
                );
              })}
            </div>
            {busy && <p className="text-sm text-[#4F46E5] mt-3 flex items-center gap-1"><SpinnerGap size={16} className="animate-spin" /> Compressing &amp; uploading…</p>}

            <Button disabled={!mandatoryQsAnswered || !requiredMediaDone || busy} onClick={runEstimate} className="rounded-xl w-full mt-7 h-12 bg-[#4F46E5] hover:bg-[#4338CA] gap-2 disabled:opacity-50" data-testid="sell-condition-continue">
              {busy ? "Calculating…" : "Get Estimate"} <ArrowRight size={16} weight="bold" />
            </Button>
          </div>
        )}

        {/* ===== STEP 2 — Offer & Details ===== */}
        {step === 2 && form.estimate && (
          <div data-testid="sell-step-offer">
            <div className="text-center" data-testid="sell-estimate">
              <Sparkle size={32} weight="fill" className="text-[#4F46E5] mx-auto" />
              <p className="text-[#475569] mt-3">Your estimated value</p>
              <div className="font-[Outfit] text-5xl font-extrabold text-[#1E293B] mt-1" data-testid="sell-estimate-price">₹{form.estimate.estimated_price.toLocaleString("en-IN")}</div>
              <span className="inline-block mt-3 px-4 py-1.5 rounded-full bg-[#4F46E5]/10 text-[#4F46E5] font-bold text-sm" data-testid="sell-estimate-tier">{form.estimate.tier_name}</span>
              <div className="mt-6 bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-4 text-left text-sm max-w-sm mx-auto">
                <div className="flex justify-between py-1"><span className="text-[#475569]">Base value</span><span className="font-semibold">₹{Number(form.estimate.base_price).toLocaleString("en-IN")}</span></div>
                {form.estimate.tier !== "scrap" && <div className="flex justify-between py-1"><span className="text-[#475569]">Condition tier</span><span className="font-semibold">{form.estimate.tier_name} ({form.estimate.tier_percentage}%)</span></div>}
                <div className="flex justify-between py-1 border-t border-[#E2E8F0] mt-1 pt-2"><span className="text-[#475569]">Estimated offer</span><span className="font-bold text-[#4F46E5]">₹{form.estimate.estimated_price.toLocaleString("en-IN")}</span></div>
              </div>
              <p className="text-xs text-[#94a3b8] mt-3 max-w-sm mx-auto">Estimated value. Final price is confirmed after physical inspection.</p>
            </div>

            <h3 className="font-[Outfit] font-bold text-lg mt-8">Your details</h3>
            <div className="space-y-3 mt-3">
              <Input placeholder="Full name" className="rounded-xl" value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="sell-name" />
              <Input placeholder="Mobile number" className="rounded-xl" value={form.phone} onChange={(e) => set({ phone: e.target.value })} data-testid="sell-phone" />
              <Input placeholder="Email" className="rounded-xl" value={form.email} onChange={(e) => { set({ email: e.target.value }); setOtpSent(false); setOtpVerified(false); }} data-testid="sell-email" />
              <textarea placeholder="Full pickup address" className="w-full rounded-xl border border-[#CBD5E1] p-3 text-sm min-h-[80px]" value={form.address} onChange={(e) => set({ address: e.target.value })} data-testid="sell-address" />
              <Input placeholder="Pincode" maxLength={6} className="rounded-xl" value={form.pincode} onChange={(e) => set({ pincode: e.target.value.replace(/\D/g, "") })} data-testid="sell-pincode" />
            </div>
            <Button className="rounded-xl w-full mt-6 h-12 bg-[#4F46E5] hover:bg-[#4338CA] gap-2" data-testid="sell-details-continue"
              onClick={async () => {
                if (!form.name || !form.phone || !form.email || !form.address) { toast.error("Please fill all details"); return; }
                if (await checkPincode()) next();
              }}>Continue <ArrowRight size={16} weight="bold" /></Button>
          </div>
        )}

        {/* ===== STEP 3 — Pickup & Confirm (Terms + OTP + Slot) ===== */}
        {step === 3 && (
          <div data-testid="sell-step-pickup">
            <h2 className="font-[Outfit] text-2xl font-bold">Schedule pickup &amp; confirm</h2>

            {/* Pickup slot */}
            <Label className="text-sm font-semibold mt-6 block">Pickup date</Label>
            <Input type="date" min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)} className="rounded-xl mt-1.5" value={form.pickup_date} onChange={(e) => set({ pickup_date: e.target.value })} data-testid="sell-pickup-date" />
            <Label className="text-sm font-semibold mt-4 block">Time slot</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              {slots.map((s) => (
                <button key={s.id} onClick={() => set({ pickup_slot: s.label })} data-testid={`sell-slot-${s.id}`}
                  className={`px-3 py-3 rounded-xl border-2 text-sm font-medium transition-all duration-200 ${form.pickup_slot === s.label ? "border-[#4F46E5] bg-[#4F46E5]/5" : "border-[#E2E8F0] hover:border-[#4F46E5]"}`}>
                  {s.label}
                </button>
              ))}
            </div>

            {/* Verify email */}
            <h3 className="font-[Outfit] font-bold text-base mt-8">Verify your email</h3>
            {otpVerified ? (
              <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#166534]" data-testid="sell-otp-verified"><CheckCircle size={18} weight="fill" /> {form.email} verified</p>
            ) : !otpSent ? (
              <div className="mt-2">
                <p className="text-[#475569] text-sm">We'll email a 6-digit code to <b>{form.email}</b>.</p>
                <Button onClick={sendOtp} disabled={busy} className="rounded-xl mt-3 h-11 bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="sell-otp-send">{busy ? "Sending…" : "Send OTP"}</Button>
              </div>
            ) : (
              <div className="mt-3 space-y-3 max-w-xs">
                <Input placeholder="Enter 6-digit OTP" maxLength={6} className="rounded-xl text-center tracking-[0.4em] text-lg" value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))} data-testid="sell-otp-input" />
                <Button onClick={verifyOtp} disabled={busy || otpCode.length !== 6} className="rounded-xl w-full h-11 bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="sell-otp-verify">{busy ? "Verifying…" : "Verify"}</Button>
                <button onClick={sendOtp} className="text-sm text-[#4F46E5] font-semibold">Resend OTP</button>
              </div>
            )}

            {/* Terms */}
            <h3 className="font-[Outfit] font-bold text-base mt-8">Terms &amp; Conditions</h3>
            <div className="mt-2 border border-[#E2E8F0] rounded-2xl p-4 max-h-[30vh] overflow-y-auto text-sm text-[#334155]" data-testid="sell-terms" dangerouslySetInnerHTML={{ __html: terms?.content || "" }} />
            <label className="flex items-start gap-2 mt-3 cursor-pointer">
              <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#4F46E5]" checked={form.agree} onChange={(e) => set({ agree: e.target.checked })} data-testid="sell-terms-checkbox" />
              <span className="text-sm text-[#475569]">I agree to the Sell Device Terms &amp; Conditions.</span>
            </label>

            <Button disabled={busy || !form.agree || !otpVerified || !form.pickup_date || !form.pickup_slot} onClick={submit} className="rounded-xl w-full mt-7 h-12 bg-[#4F46E5] hover:bg-[#4338CA] gap-2 disabled:opacity-50" data-testid="sell-submit">
              {busy ? "Submitting…" : "Confirm Pickup Request"}
            </Button>
          </div>
        )}
      </div>
    </StoreLayout>
  );
}
